import { PLATFORM_STATS } from '~/lib/marketing/site';

/**
 * Bilingual content for the landing-page sections that were ADDED on top of
 * the ported Flow page (trust bar, stats band, What's New strip, FAQ teaser,
 * market trust row). Ported sections keep their `landing` i18n namespace keys.
 *
 * What's New highlights are a curated snapshot of
 * app/home/[account]/whats-new/_lib/changelog.json, hand-picked (not
 * imported) so the 99KB changelog never enters the client bundle and the
 * landing always shows the three strongest recent entries.
 */

export interface Localized {
  en: string;
  ar: string;
}

export const TRUST_BAR = {
  heading: {
    en: 'Some of the clubs, studios and academies on Flow',
    ar: 'بعض الأندية والاستوديوهات والأكاديميات التي تعمل على Flow',
  },
  stats: [
    {
      value: PLATFORM_STATS.venues,
      label: { en: 'Venues', ar: 'منشأة' },
    },
    {
      value: PLATFORM_STATS.bookings,
      label: { en: 'Bookings processed', ar: 'حجز تمت معالجته' },
    },
    {
      value: PLATFORM_STATS.players,
      label: { en: 'Clients & members', ar: 'عميل وعضو' },
    },
    {
      value: String(PLATFORM_STATS.countries),
      label: { en: 'Countries', ar: 'دول' },
    },
  ],
} as const;

/** Client logos shown in the trust marquee (assets already in the repo). */
export const TRUST_LOGOS = [
  { name: 'Deem Padel', file: 'deem-padel.png' },
  { name: 'Dunes Racket Club', file: 'dunes-racket-club.png' },
  { name: 'Padel District', file: 'padel-district-jeddah.png' },
  { name: 'B Padel', file: 'b-padel.jpeg' },
  { name: 'The Racquet Space', file: 'the-racquet-space.png' },
  { name: 'Red Sea Sports Club', file: 'red-sea-sports-club.jpg' },
  { name: 'Ryze Tennis Academy', file: 'ryze-tennis-academy.png' },
  { name: 'Ballers', file: 'ballers.jpg' },
  { name: 'Net Tennis Academy', file: 'net-tennis-academy.png' },
  { name: 'Gaby Reca Academy', file: 'gaby-reca-academy.png' },
  { name: 'Frame Studio', file: 'frame-studio.png' },
  { name: 'The Pilates Room', file: 'the-pilates-room.png' },
  { name: 'Refine Studio', file: 'refine-studio.png' },
  { name: 'OIA', file: 'oia.png' },
  { name: 'Flex&Flow', file: 'flexflow.png' },
  { name: 'The Lab Court', file: 'the-lab-court.png' },
] as const;

/**
 * Logos drawn in a light colour on a transparent background. They vanish on
 * the sand/bone surfaces, so light-surface renderers flatten them to ink.
 */
export const LIGHT_LOGO_FILES: ReadonlySet<string> = new Set(['frame-studio.png']);

export const GCC_TRUST = {
  label: { en: 'Built for your market', ar: 'مصممة لسوقك' },
  heading: {
    en: 'Ready for your market from day one',
    ar: 'جاهزة لسوقك من اليوم الأول',
  },
  items: [
    {
      title: { en: 'Truly multilingual', ar: 'متعددة اللغات فعلًا' },
      description: {
        en: 'Every customer surface ships in your language, including full Arabic with real right-to-left layout: booking pages, WhatsApp messages, receipts and reminders.',
        ar: 'كل واجهات العملاء متوفرة بلغة عملائك، بما في ذلك العربية بواجهة كاملة من اليمين إلى اليسار: صفحات الحجز ورسائل واتساب والإيصالات والتذكيرات.',
      },
    },
    {
      title: { en: 'Local payment methods', ar: 'وسائل دفع محلية' },
      description: {
        en: 'Cards and Apple Pay through licensed gateways such as Stripe, plus local methods such as mada, with payouts in your currency to your bank account.',
        ar: 'البطاقات وApple Pay عبر بوابات مرخصة مثل Stripe، إضافة إلى وسائل محلية مثل مدى، مع تحويل المستحقات بعملتك إلى حسابك البنكي.',
      },
    },
    {
      title: { en: 'Accounting and e-invoicing', ar: 'المحاسبة والفوترة الإلكترونية' },
      description: {
        en: 'Every paid booking, package and membership syncs to Oracle, Odoo, Zoho Books or your regional system as an invoice, including ZATCA-compliant e-invoices in Saudi Arabia.',
        ar: 'كل حجز وباقة وعضوية مدفوعة تتزامن كفاتورة مع Oracle أو Odoo أو Zoho Books أو نظامك المحاسبي المحلي، بما فيها الفواتير الإلكترونية المتوافقة مع هيئة الزكاة والضريبة والجمارك في السعودية.',
      },
    },
    {
      title: { en: 'WhatsApp-native', ar: 'واتساب مدمج' },
      description: {
        en: `Official WhatsApp Business API for confirmations, reminders and an AI concierge, with ${PLATFORM_STATS.whatsappMessages} messages delivered.`,
        ar: `واجهة واتساب للأعمال الرسمية للتأكيدات والتذكيرات ومساعد ذكي، وقد تم إرسال ${PLATFORM_STATS.whatsappMessages} رسالة.`,
      },
    },
  ],
} as const;

export const WHATS_NEW_STRIP = {
  label: { en: "What's new", ar: 'جديد المنصة' },
  heading: {
    en: 'A platform that ships every week',
    ar: 'منصة تتطور كل أسبوع',
  },
  description: {
    en: 'Flow improves continuously. These landed in the last few weeks.',
    ar: 'تتطور Flow باستمرار، وهذه أحدث الإضافات خلال الأسابيع الماضية.',
  },
  viewAll: { en: 'See the full changelog', ar: 'عرض سجل التحديثات كاملًا' },
  entries: [
    {
      title: {
        en: 'Import your customers and package balances',
        ar: 'استورد عملاءك وأرصدة باقاتهم',
      },
      description: {
        en: 'Switch from any system without losing history: upload a spreadsheet and Flow migrates customers and remaining credits.',
        ar: 'انتقل من أي نظام دون فقدان البيانات: ارفع ملفًا واحدًا وتتولى Flow نقل العملاء وأرصدتهم المتبقية.',
      },
      date: '2026-07-20',
    },
    {
      title: {
        en: 'Odoo accounting & e-invoicing',
        ar: 'محاسبة أودو والفوترة الإلكترونية',
      },
      description: {
        en: 'Connect Odoo alongside Wafeq for automated, ZATCA-compliant accounting.',
        ar: 'اربط أودو إلى جانب وافق لمحاسبة آلية متوافقة مع هيئة الزكاة والضريبة والجمارك.',
      },
      date: '2026-07-24',
    },
    {
      title: {
        en: 'Set up a discount while booking',
        ar: 'أنشئ خصمًا أثناء الحجز',
      },
      description: {
        en: 'Create and apply a discount inline in the booking drawer, with no detour through settings.',
        ar: 'أنشئ الخصم وطبّقه مباشرة من نافذة الحجز، دون المرور بالإعدادات.',
      },
      date: '2026-08-04',
    },
  ],
} as const;

export const CONTACT_SECTION = {
  label: { en: 'Contact', ar: 'تواصل معنا' },
  heading: { en: 'Talk to us', ar: 'كلّمنا' },
  description: {
    en: 'Tell us about your venue and we will get back to you the same day, or pick a time that suits you directly.',
    ar: 'أخبرنا عن منشأتك وسنعاود التواصل معك في نفس اليوم، أو اختر موعدًا يناسبك مباشرة.',
  },
  bookAppointment: { en: 'Book an appointment', ar: 'احجز موعدًا' },
  form: {
    name: { en: 'Name', ar: 'الاسم' },
    email: { en: 'Email', ar: 'البريد الإلكتروني' },
    phone: { en: 'Phone / WhatsApp (optional)', ar: 'الجوال / واتساب (اختياري)' },
    venue: { en: 'Venue or business name (optional)', ar: 'اسم المنشأة (اختياري)' },
    message: { en: 'How can we help?', ar: 'كيف نقدر نخدمك؟' },
    submit: { en: 'Send message', ar: 'أرسل الرسالة' },
    success: {
      en: 'Thanks, we received your message and will reply shortly.',
      ar: 'شكرًا لك، وصلتنا رسالتك وسنرد عليك قريبًا.',
    },
    error: {
      en: 'Something went wrong. Please try again or email us directly.',
      ar: 'حدث خطأ ما. حاول مرة أخرى أو راسلنا مباشرة عبر البريد.',
    },
  },
} as const;

export const FAQ_TEASER = {
  label: { en: 'FAQ', ar: 'الأسئلة الشائعة' },
  heading: {
    en: 'Questions venues ask us',
    ar: 'أسئلة تصلنا من المنشآت',
  },
  viewAll: { en: 'See all questions', ar: 'عرض كل الأسئلة' },
} as const;
